import React, { useEffect, useState } from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";

const COLORS = {
  primary: '#0760ad',
  secondary: '#098a48',
  tertiary: '#118a94',
  white: '#FFFFFF',
  darkText: '#1a365d',
  lightText: '#4a5568'
};

const DEFAULT_DATA = {
  title: "Your Health, Our Priority",
  subtitle: "Warm Wellness Center's Commitment to Advanced Health Care Solutions",
  ctaPrimary: { 
    text: "Book Appointment", 
    link: "" 
  },
  ctaSecondary: { 
    text: "Explore Services", 
    link: "" 
  },
  heroImage: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
  services: [
    { name: "Diagnostic Imaging", icon: "🖥️" },
    { name: "Preventive Care", icon: "🩺" },
    { name: "Specialist Consultations", icon: "👨‍⚕️" }
  ],
  stats: [
    { value: "99%", label: "Accuracy Rate" },
    { value: "24/7", label: "Emergency Support" },
    { value: "500+", label: "Medical Professionals" }
  ]
};

const OrbitingCircle = ({ size, color, delay, radius }) => {
  return (
    <motion.div
      initial={{ x: -radius, y: 0 }}
      animate={{
        x: [-radius, radius, -radius],
        y: [0, 0, 0],
        transition: {
          duration: 8,
          ease: "linear",
          repeat: Infinity,
          delay: delay
        }
      }}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        position: 'absolute',
        backgroundColor: color,
        opacity: 0.2
      }}
    />
  );
};

const Hero = () => {
  const [heroData, setHeroData] = useState(DEFAULT_DATA);
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.1 });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/home/hero`);
        if (response.ok) {
          const apiData = await response.json();
          setHeroData({
            ...DEFAULT_DATA,
            ...apiData
          });
        }
      } catch (error) {
        console.error("Failed to fetch hero data", error);
      }
    };
    
    fetchData();
  }, []);

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  const primaryGradient = `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.tertiary} 100%)`;
  const secondaryGradient = `linear-gradient(135deg, ${COLORS.white} 0%, ${COLORS.white} 100%)`;

  return (
    <section 
      ref={ref}
      className="py-20 md:py-28 bg-white overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${COLORS.white} 0%, #f7fafc 100%)`
      }}
    >
      <div className="container mx-auto px-4 flex flex-col md:flex-row items-center">
        {/* Text Content */}
        <div className="md:w-1/2 mb-16 md:mb-0 md:pr-10">
          <motion.div
            initial="hidden"
            animate={controls}
            variants={{
              visible: {
                opacity: 1,
                y: 0,
                transition: { duration: 0.6 }
              },
              hidden: { opacity: 0, y: 20 }
            }}
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight" style={{ color: COLORS.darkText }}>
              {heroData.title}
            </h1>
            
            <p className="text-xl mb-8" style={{ color: COLORS.lightText }}>
              {heroData.subtitle}
            </p>
            
            {/* Services */}
            <div className="mb-10">
              <h3 className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ color: COLORS.primary }}>
                Our Core Services
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {heroData.services.map((service, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ y: -3 }}
                    className="flex items-center p-4 rounded-lg border border-gray-100 bg-white shadow-sm"
                  >
                    <span className="text-2xl mr-3">{service.icon}</span>
                    <span className="font-medium">{service.name}</span>
                  </motion.div>
                ))}
              </div>
            </div>
            
            {/* Stats */}
            <div className="flex gap-6 mb-10">
              {heroData.stats.map((stat, index) => (
                <div key={index}>
                  <p className="text-3xl font-bold mb-1" style={{ color: COLORS.primary }}>
                    {stat.value}
                  </p>
                  <p className="text-sm text-gray-500">{stat.label}</p>
                </div>
              ))}
            </div>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={heroData.ctaPrimary.link}>
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                className="px-8 py-3 rounded-lg font-medium text-white shadow-lg"
                style={{ 
                  background: primaryGradient,
                  boxShadow: `0 4px 20px ${COLORS.primary}40`
                }}
              >
                {heroData.ctaPrimary.text}
              </motion.button>
              </a>
              
              <a href={heroData.ctaSecondary.link}>

              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                className="px-8 py-3 rounded-lg font-medium border-2 shadow-sm"
                style={{ 
                  borderColor: COLORS.primary,
                  color: COLORS.primary,
                  background: secondaryGradient
                }}
              >
                {heroData.ctaSecondary.text}
              </motion.button>
              </a>
            </div>
          </motion.div>
        </div>
        
        {/* Animated Medical Image */}
        <div className="md:w-1/2 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ 
              opacity: 1, 
              scale: 1,
              transition: { delay: 0.2, duration: 0.6 }
            }}
            className="relative"
          >
            {/* Orbiting animation elements */}
            <div className="absolute inset-0 flex items-center justify-center">
              <OrbitingCircle size="80px" color={COLORS.primary} delay={0} radius="120px" />
              <OrbitingCircle size="60px" color={COLORS.secondary} delay={2} radius="160px" />
              <OrbitingCircle size="40px" color={COLORS.tertiary} delay={4} radius="200px" />
            </div>
            
            {/* Main image with floating effect */}
            <motion.div
              animate={{
                y: [0, -15, 0],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="relative z-10 rounded-2xl overflow-hidden border-4 border-white shadow-2xl"
              style={{
                boxShadow: "0 25px 50px -12px rgba(7, 96, 173, 0.25)"
              }}
            >
              <img 
                src={heroData.heroImage} 
                alt="Advanced medical imaging" 
                className="w-full h-full lg:object-cover object-center"
              />
              
              {/* Image overlay */}
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/40" />
              
            
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;